/**
 * The handbook (M1.1, docs/11-interface.md): one authored, data-fed reference
 * page per system, opened contextually from any overlay's `?` and from any
 * figure that names a page. The tooltip is the one-line half of the job; this
 * is the paragraph half. Pages are keyed by id and every package after M1.1
 * adds its own; where a page can show the player's *own* numbers cheaply it
 * does, read from the store and never computed here.
 */

import { useSyncExternalStore, type ReactNode } from 'react';
import type { CampaignUi } from '../bridge';
import { d0OfMilli, int, money, pctOfMille, timesOfMille } from './format';
import { closeHandbook, openHandbook, usePanels } from './panels';
import { snapshot, subscribe } from './store';

interface Page {
  title: string;
  body: (c: CampaignUi | null) => ReactNode;
}

function Link({ to, children }: { to: string; children: ReactNode }) {
  return (
    <button className="link" onClick={() => openHandbook(to)}>
      {children}
    </button>
  );
}

const PAGES: Record<string, Page> = {
  contents: {
    title: 'Handbook',
    body: () => (
      <>
        <p>
          One page per system, in the design's own words, with your numbers filled in where the store has them. The tooltip on a
          figure is the one-line version of the same explanation; the <em>?</em> beside a figure lands here.
        </p>
        <h3>Pages</h3>
        <p>
          <Link to="lines">Lines</Link> — a production line: a curve, its costs, a dial.
        </p>
        <p>
          <Link to="ledger">The ledger</Link> — the lines of the week and how cash decomposes.
        </p>
        <p>
          <Link to="technology">Technology</Link> — the ladder, research, licensing, fab blocks.
        </p>
        <p>
          <Link to="research">The research pipeline</Link> — the two queues, funding levels, engineers, mask revisions.
        </p>
        <p>
          <Link to="blocks">Blocks and the IP library</Link> — variants, loadouts, porting, what a design compiles to.
        </p>
        <p>
          <Link to="markets">Markets</Link> — demand, ignition, the index, the logit, sockets.
        </p>
        <p>
          <Link to="clocks">The two clocks, and saves</Link> — watch against forward, the seam, the hash.
        </p>
        <p>
          <Link to="floor">The floor</Link> — where the floor's own panels are.
        </p>
        <p>
          <Link to="machines">Machines</Link> — models, condition, maintenance, overhaul.
        </p>
        <p>
          <Link to="quality">Output quality</Link> — capability × experience, latent until final test.
        </p>
        <p>
          <Link to="grades">The bin ladder</Link> — grades as priced variants of the product.
        </p>
        <p>
          <Link to="shifts">Shifts</Link> — staffed hours, wages by coverage, the night's cost.
        </p>
        <p>
          <Link to="used-market">The used market</Link> — the cycle's other face, priced.
        </p>
      </>
    ),
  },

  lines: {
    title: 'Lines',
    body: (c) => {
      const lines = c?.firm.lines ?? [];
      return (
        <>
          <p>
            <strong>A production line is the unit the economy sees.</strong> It is a capacity slot: a calibration curve — wafers out,
            cycle time and the D₀ modifier against the release dial — plus its costs, plus the product assigned to it. The weekly tick
            consumes lines and nothing below them; it cannot tell whether the curve behind a line was measured off a floor or authored
            as data. That blindness is the rule that lets fast-forward work at all (<Link to="clocks">the two clocks</Link>).
          </p>
          <h3>Three kinds</h3>
          <p>
            <strong>A fab block</strong> is M0's abstract capacity: authored wafers a week and opex per node, built and retrofitted from
            the <Link to="technology">Technology</Link> overlay. It has no dial.
          </p>
          <p>
            <strong>A rented line</strong> is authored under <code>data/</code>: a starts cap, a cycle time, a node behind the frontier and
            a fixed, mediocre quality nobody can improve. Priced per wafer started plus a weekly commitment. It is the underdog opening's
            whole production base and the baseline every later reading is legible against.
          </p>
          <p>
            <strong>Your fab</strong> is the floor you are standing in, measured into its curve by the site sim and crossing the seam
            every week. A <em>watched</em> week is booked as observed — the wafers the floor actually finished; a <em>forwarded</em>{' '}
            week is read off the curve. Its dial follows the floor's own release rate (set on the scoreboard), which is why the
            dashboard shows it read-only.
          </p>
          <h3>Two refusals</h3>
          <p>
            A line with no curve has <em>no answer</em>: it ships nothing and books its fixed cost, until the floor has measured a
            first point. And when the floor changes — a machine moved, a shift resized — its curve is thrown away, because it would
            describe a fab that no longer exists; until it is re-measured the dashboard marks the line <em>stale</em> and the tick
            runs it on the last curve it had.
          </p>
          <h3>Your lines</h3>
          {lines.length === 0 ? (
            <p className="muted">No lines yet.</p>
          ) : (
            <table>
              <thead>
                <tr>
                  <th>line</th>
                  <th>kind</th>
                  <th>node</th>
                  <th>dial</th>
                  <th>curve</th>
                </tr>
              </thead>
              <tbody>
                {lines.map((l) => (
                  <tr key={l.id}>
                    <td>{l.name}</td>
                    <td>{l.sourceLabel}</td>
                    <td>{l.nodeName}</td>
                    <td>{l.startsPerDay}/day</td>
                    <td>
                      {l.curve.length === 0
                        ? l.hasCurve
                          ? `${int(l.capacityWafersPerWeek)} wafers/wk (authored)`
                          : 'no curve'
                        : l.curve
                            .map((p) => `${p.startsPerDay}→${p.wafersOutPerWeek}/wk (${p.cycleHours.toFixed(0)} h, D₀ ${timesOfMille(p.d0Mille)})`)
                            .join(' · ')}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          <p>
            Costs are read against the <Link to="ledger">ledger</Link>: fixed a week plus materials on wafers <em>started</em>, because
            that is when a fab buys silicon.
          </p>
        </>
      );
    },
  },

  ledger: {
    title: 'The ledger',
    body: (c) => {
      const l = c?.firm.ledger;
      return (
        <>
          <p>
            <strong>Every week closes on a ledger</strong>, and cash after the week is opening cash plus its lines. Nothing on the
            dashboard is a number the ledger did not book; the tooltip on cash opens into last week's lines, and each line into
            what made it.
          </p>
          <h3>The lines of the week</h3>
          <p>
            <em>Base income</em> — the founding contract or subsidy that keeps a young firm breathing. <em>Market revenue</em>, by
            category — units the logit awarded you × your asking price (<Link to="markets">markets</Link>). <em>Overhead</em> — the
            firm's fixed weekly cost. <em>Per line</em> — fixed a week plus materials on wafers started, one line each (
            <Link to="lines">lines</Link>). <em>R&amp;D instalment</em> — the running project's weekly share (
            <Link to="technology">technology</Link>). <em>Interest</em> on whatever is drawn against the credit line. <em>Capital</em> —
            fab blocks and retrofits, booked when committed.
          </p>
          {l && (
            <table>
              <tbody>
                <tr>
                  <td>opening cash</td>
                  <td>{money(l.openingCash)}</td>
                </tr>
                <tr>
                  <td>base income</td>
                  <td>{money(l.baseIncome)}</td>
                </tr>
                <tr>
                  <td>market revenue</td>
                  <td>{money(l.revenue)}</td>
                </tr>
                <tr>
                  <td>overhead</td>
                  <td>{money(l.overhead)}</td>
                </tr>
                <tr>
                  <td>line costs</td>
                  <td>{money(l.lineCosts)}</td>
                </tr>
                <tr>
                  <td>R&amp;D</td>
                  <td>{money(l.rnd)}</td>
                </tr>
                <tr>
                  <td>interest</td>
                  <td>{money(l.interest)}</td>
                </tr>
                <tr>
                  <td>capital</td>
                  <td>{money(l.capex)}</td>
                </tr>
                <tr>
                  <td>
                    <strong>swing</strong>
                  </td>
                  <td>
                    <strong>{money(l.swing)}</strong>
                  </td>
                </tr>
              </tbody>
            </table>
          )}
          <h3>Quarters</h3>
          <p>
            Weeks roll up into a quarterly P&amp;L — revenue, operating expenses, R&amp;D, interest, net, and capital spent — the
            table at the bottom of the dashboard, the quarter to date first.
          </p>
          <h3>The credit line</h3>
          <p>
            Cash may go below zero as far as the credit limit; what is drawn accrues interest and is repaid from cash as cash allows.
            Past the limit the firm is insolvent and <strong>the run ends</strong>.
            {c && (
              <>
                {' '}
                Yours: {money(c.firm.debt)} drawn against {money(c.firm.creditLimit)}.
              </>
            )}
          </p>
        </>
      );
    },
  },

  technology: {
    title: 'Technology',
    body: (c) => {
      const nodes = c?.tech.nodes ?? [];
      return (
        <>
          <p>
            <strong>The ladder is the industry's process nodes in order</strong>, each authored with a feature size, a wafer size,
            what it costs to research or license, and what a fab block at it costs to build and run. Your <em>capability</em> is the
            best rung you can build to; the <em>frontier</em> is the best anyone can, and it marches on its own.
          </p>
          <h3>Research</h3>
          <p>
            A node rung is a <Link to="research">project</Link> on the Process R&amp;D queue: its cost is paid in weekly instalments
            over the planned weeks, each week rolls against the node's slip chance, and a slipped week costs the instalment again.
            One node project at a time — the ladder is climbed a rung at a time. Cancel and the spend is lost.
          </p>
          <h3>Licensing</h3>
          <p>
            A node the frontier has moved past can be licensed instead: a lump sum now and a transfer of a few weeks — cheaper than
            research, never the leading edge. Nothing is licensable while the frontier is too close.
          </p>
          <h3>Fab blocks and retrofits</h3>
          <p>
            A block is abstract capacity at a node: capex up front, build weeks, then authored wafers a week at authored opex. An
            operating block can be retrofitted one rung up once you have the capability. Both are lines (<Link to="lines">lines</Link>
            ) and both appear on the dashboard.
          </p>
          <h3>The ladder</h3>
          {nodes.length > 0 && (
            <table>
              <thead>
                <tr>
                  <th>node</th>
                  <th>feature</th>
                  <th>R&amp;D</th>
                  <th>license</th>
                </tr>
              </thead>
              <tbody>
                {nodes.map((n) => (
                  <tr key={n.id}>
                    <td>
                      {n.name}
                      {n.attained ? ' ✓' : ''}
                      {n.isFrontier ? ' ★' : ''}
                    </td>
                    <td>{n.feature}</td>
                    <td>
                      {money(n.rndCost)} · {n.rndWeeks} wk
                    </td>
                    <td>
                      {money(n.licenseCost)} · {n.licenseWeeks} wk
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          <p className="muted">
            Funding levels, the queues and the design desk live on the <Link to="research">research pipeline</Link> page; the
            paradigm race is late-game.
          </p>
        </>
      );
    },
  },

  research: {
    title: 'The research pipeline',
    body: (c) => {
      const wage = c?.tech.engineerWage ?? 0;
      return (
        <>
          <p>
            <strong>One project machinery, two divisions.</strong> Process R&amp;D runs node rungs and licensing; Product Design
            develops <Link to="blocks">block variants</Link>, ports them across node families, revises shipped masks and engineers
            new designs to tape-out. A project is a target, a team, and a <em>funding level</em>; the division's queue is where
            direction lives — which projects exist at all.
          </p>
          <h3>Funding levels</h3>
          <p>
            <em>Steady</em> is the baseline schedule. <em>Crash</em> runs half again the pace at more than double the weekly burn —
            and slips more than twice as often, because a schedule bought with money is a schedule held together with money.
            <em> Trickle</em> is cheap, slow and safe. A slipped week burns its instalment and moves nothing; the harness pins that
            a crash-funded rung slips more often than a steady one.
          </p>
          <h3>Engineers</h3>
          <p>
            Each division has a pool, paid {wage > 0 ? <strong>${wage}/wk each</strong> : 'a weekly wage each'}, working or
            idle. The queue staffs <strong>top-first</strong>: the first project takes its full ask, the next takes what is left,
            and an understaffed project burns and progresses at its staffed fraction. Reordering the queue is therefore a real
            decision, and so is the headcount.
          </p>
          <h3>Mask revisions</h3>
          <p>
            A small Product Design project on a <em>shipped</em> product: a revision is learning from the line, so it requires
            wafers behind it. Each revision steps the product's yield and parametric quality up, with diminishing returns — the
            first buys the most, the mid-life product gets a decision.
          </p>
        </>
      );
    },
  },

  blocks: {
    title: 'Blocks and the IP library',
    body: (c) => {
      const library = c?.tech.library ?? [];
      return (
        <>
          <p>
            <strong>A spec is a loadout, filled from what the company owns.</strong> An architecture generation defines slots — an
            MPU wants a register file, an ALU, control, a bus interface, and optionally cache and an FPU — and each slot takes a
            <em> block variant</em> from your IP library. Variants enter the library one way only: a Product Design project. Nothing
            arrives free with a generation, so what the design desk can build is what R&amp;D was pointed at.
          </p>
          <h3>What a loadout compiles to</h3>
          <p>
            <em>Die area</em> is the blocks' cells at the node's feature size, plus layout overhead — silicon falls out of the node
            the design lands on. <em>Clock</em> is the generation's baseline dragged or held by each variant's timing character
            (compact variants are smaller and slower; clock-tolerant ones larger and faster). The <em>market dimensions</em> sum the
            blocks' contributions: floating-point is essentially zero without an FPU slot filled, integer reads the ALU tier and the
            cache, a memory part's capacity <em>is</em> its array tier. Power runs off block power × clock, so the fast design pays
            on efficiency.
          </p>
          <h3>Families and porting</h3>
          <p>
            A variant is implemented for one node <em>family</em> — NMOS, CMOS — and a design may only use library entries of its
            node's family. Porting a variant to another family is a smaller project than redeveloping it; the CMOS transition is
            where the whole library earns its keep or ages out.
          </p>
          <h3>Reuse</h3>
          <p>
            A design project is discounted for every slot filled with a <em>proven</em> variant — one a shipped product has carried
            — and stretched by every slot filled with one the team has never shipped. Over the years the library becomes company
            DNA{library.length > 0 ? <> — yours holds {library.length} entr{library.length === 1 ? 'y' : 'ies'} today</> : null}.
          </p>
        </>
      );
    },
  },

  markets: {
    title: 'Markets',
    body: (c) => {
      const markets = c?.markets ?? [];
      return (
        <>
          <p>
            <strong>Each category is a demand S-curve</strong>: latent until it <em>ignites</em>, then growing along an authored
            curve toward saturation, with the macro overlay of whatever event is active pressing on it this week. Demand is units;
            the <em>spot index</em> is what a unit is worth this week, and it is where the cycle lives — booms and busts move it,
            and it floors at an authored price.
          </p>
          <h3>Asking price</h3>
          <p>
            You price a design as a multiple of its category's index, in per-mille: 1000‰ is the index, 900‰ is ten percent under
            it. The dashboard's −10% / +10% buttons move that multiple.
          </p>
          <h3>The logit</h3>
          <p>
            Buyers split demand among every offer by a logit over the category's <em>dimension vector</em> and price (see{' '}
            <Link to="dimensions">dimensions</Link>): every axis a product scores on, weighed by what this era's
            buyers care about, against the ambient reference at the frontier. Each category weighs its axes differently — and the
            weights drift with the calendar:
          </p>
          {markets.length > 0 && (
            <table>
              <thead>
                <tr>
                  <th>category</th>
                  <th>axes (this week's weights)</th>
                  <th>price</th>
                  <th>binning</th>
                  <th>your share</th>
                </tr>
              </thead>
              <tbody>
                {markets.map((m) => (
                  <tr key={m.id}>
                    <td>{m.name}</td>
                    <td>{m.dimensions.map((d) => `${d.name} ${pctOfMille(d.weightMille, 0)}`).join(', ')}</td>
                    <td>{pctOfMille(m.priceWeightMille, 0)}</td>
                    <td>{m.binning}</td>
                    <td>{pctOfMille(m.playerShareMille)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          <p>
            Share is your units sold over the category's demand. Unsold units go to the warehouse where the category carries{' '}
            <Link to="inventory">inventory</Link>, and are scrapped where it does not.
          </p>
          <h3>Sockets and contracts</h3>
          <p>
            Now and then a category opens a <em>design-win socket</em>: so many units a week for so many weeks, a minimum composite
            index, bids closing on a date. Bid a design at a price multiple; at resolution it is scored against the ambient
            industry — with the socket buyer's <em>own</em> weight vector where it has one: the military socket barely reads
            price, the calculator socket reads almost nothing else. A win is a contract — the stable leg through the cycle.
          </p>
          <h3>Salvage bins</h3>
          <p>
            In speed-graded categories a die with a single defect can ship as a down-bin rather than scrap; the line reports those
            as salvage dies. M1.3 makes grades priced variants of the product.
          </p>
        </>
      );
    },
  },

  clocks: {
    title: 'The two clocks, and saves',
    body: (c) => (
      <>
        <p>
          <strong>The industry ticks weekly; the floor runs in theater time, and the two are coupled 7:1.</strong> One floor
          tick is 3 theater seconds and 1 real second is 30 theater seconds at 1×. One calendar week is <em>one floor day</em>
          (28,800 ticks — 48 minutes of watching at 1×, six at 8×): the day the floor runs stands for the week, and what it
          finishes is booked seven times over. <em>Watch</em> runs the floor and closes a week every floor day. <em>Forward</em>
          leaves the floor standing still and
          advances the calendar on the line's curve — the site sim is never consulted, which is the property the whole comparison
          rests on. Paused advances nothing.
        </p>
        <h3>The seam</h3>
        <p>
          The measured line is where the two clocks meet: the floor's calibration curve is what forward reads, and a watched week
          is what the curve is checked against. If the two disagree beyond the harness's bands the seam leaks, and the{' '}
          <em>glued hash</em> printed in the bar and the HUD is how a divergence between native and WASM, watched and forwarded,
          becomes visible.
          {c && (
            <>
              {' '}
              This campaign: {c.watchedWeeks} weeks watched, {c.forwardedWeeks} forwarded, hash <code>{c.hash}</code>.
            </>
          )}
        </p>
        <h3>Saves</h3>
        <p>
          The campaign autosaves to the origin's private file system (OPFS), falling back to IndexedDB where OPFS cannot write.
          Browser storage is evictable, so <em>export</em> writes the same document to a file you own and <em>import</em> reads it
          back. The format is <code>yield.campaign/5</code>: one JSON document with its own hash, and a save reloaded hashes
          identically to the campaign that wrote it.
        </p>
      </>
    ),
  },

  machines: {
    title: 'Machines',
    body: () => {
      return (
        <>
          <p>
            A recipe step names a <strong>tool type</strong> — an aligner, a furnace — and a type is answered by a{' '}
            <strong>model</strong>: a vendor's machine, with its own throughput, changeover, reliability, price, upkeep and
            cleanliness. Two units of one model behave alike; <em>which model you buy</em> is the decision, and no model is
            best everywhere — the cheap one is cheap capital that breaks and sheds particles, the premium one buys uptime
            and cleanliness with money.
          </p>
          <p>
            A bought machine is not capacity yet: it stands on its footprint through <strong>install and qualification
            weeks</strong> doing nothing, which is why the bay you leave for the second aligner is planning, not waste.
            Once it works, its <strong>condition</strong> decays with the wafers it runs — a tired machine fails more and
            dirties the D₀ modifier — at a rate set by its <strong>maintenance</strong>: keeping a machine up costs the
            full upkeep line; neglecting it is cheaper by the week and wears it three times as fast. An{' '}
            <strong>overhaul</strong> strips it down for weeks and a share of its list price, and it comes back as new.
          </p>
          <p>
            Condition reaches the calibration curve in <em>bands</em>: drift inside a band is absorbed by the merged
            windows, crossing one re-measures the fab, so the curve never quietly describes a fleet that no longer
            exists. Select any machine in inspect mode for its levers.
          </p>
        </>
      );
    },
  },

  'used-market': {
    title: 'The used market',
    body: (c) => {
      const m = c?.usedMarket;
      return (
        <>
          <p>
            New machines are paid at list. Used ones come from firms that <strong>died</strong>, fabs that were{' '}
            <strong>retooled</strong>, and capacity <strong>mothballed</strong> in a glut — so the supply arrives in busts,
            as it did in 1974–75. The price falls as the market floods and climbs as booms drain it
            {m != null ? (
              <>
                {' '}
                — right now <strong>{m.supplyFabs.toFixed(1)} fabs&apos; worth</strong> is on the market and used gear goes
                for <strong>{Math.round(m.priceMille / 10)}% of list</strong>
              </>
            ) : null}
            . Buying capacity at the bottom of a crash is the master move; the catch is what a used machine is.
          </p>
          <p>
            What it is: cheaper capital, <strong>unknown condition until it is installed and qualified</strong> (which
            takes twice as long — nobody from the vendor is coming), no vendor spares so repairs take half again as long,
            and whatever wear its last owner put on it. A bad roll is not a write-off: an overhaul makes it new, at a
            price — so the gamble has a floor, and the floor costs money.
          </p>
          <p>
            Selling works the same market: a machine you no longer want fetches its list at the market&apos;s rate, scaled
            by its condition, and the proceeds come back onto the ledger as capital returned. A demolition, by contrast,
            refunds nothing.
          </p>
        </>
      );
    },
  },

  quality: {
    title: 'Output quality',
    body: (c) => {
      const site = c?.siteLine != null ? c.firm.lines.find((l) => l.index === c.siteLine) : undefined;
      const a = site?.assignments[0];
      return (
        <>
          <p>
            A line's output is two numbers, and the fab earns both. <strong>Yield</strong> is how many dies work at all —
            the node's learning-curve D₀ times the line's own modifier. <strong>Parametric quality</strong> is how much of
            the design's ceiling the good dies reach: the good dies spread out below the clock the design could hit, and
            quality decides how high and how tight that spread sits. Worse execution both drops the middle and widens the
            tails — the shape of a bad ramp.
          </p>
          <p>
            Quality is <strong>capability × experience</strong>. Capability is the machines: each model's authored process
            capability, worsened by wear, weighted by how often the route visits it — litho counts most. It rides on the
            calibration curve like cleanliness does, so the rented line's is fixed and mediocre by contract, and your
            fab's is whatever you bought and however you keep it. <strong>Experience</strong> is the ramp, per product{' '}
            <em>per line</em>: volume narrows the spread, and moving a product to a line that has never run it starts the
            ramp over — which is what makes assignment a commitment rather than a toggle.
            {a != null ? (
              <>
                {' '}
                Your fab reads <strong>{(a.qualityMille / 10).toFixed(0)}%</strong> on {a.product} right now, off{' '}
                {int(a.experienceWafers)} wafers of experience.
              </>
            ) : null}
          </p>
          <p>
            Quality is <strong>slow knowledge</strong>: a lot's grade split is fixed by the line's state while it was
            processed and revealed only at final test, a full cycle time later — the weekly report carries the same lag.
            Throughput you can know fast; what the wafers <em>became</em>, you learn when they come out. See{' '}
            <Link to="grades">grades</Link> for what the split sells as, and <Link to="shifts">shifts</Link> for the
            pattern's quality cost.
          </p>
        </>
      );
    },
  },

  grades: {
    title: 'The bin ladder',
    body: () => (
      <>
        <p>
          Each category authors a <strong>bin ladder</strong> — clock steps for a processor, access-time grades for a
          DRAM. Every good die lands in a grade by where its realized speed falls against the ladder; the last grade is
          the catch-all, so a slow part is a cheap part, never scrap. A grade is a <strong>sellable variant</strong>: it
          is rated at its own performance and offered at its own ask, priced as a share of the flagship price — your
          lever, per grade, on the Dashboard's product table.
        </p>
        <p>
          Your own variants compete with each other in the same market: a down-bin priced too near the flagship eats the
          flagship's share, and clearance-pricing slow parts is the honest remedy for a bad ramp — at exactly the price
          the buyers' logit charges for it. This is different from <em>salvage</em>: salvage is defective dies shipping
          as down-bins (a defect that kills a cache bank leaves a slower sellable part); the ladder splits the{' '}
          <em>good</em> dies by how fast they turned out.
        </p>
      </>
    ),
  },

  shifts: {
    title: 'Shifts',
    body: () => (
      <>
        <p>
          The floor works <strong>staffed hours only</strong>: one, two or three eight-hour shifts a day. A one-shift
          fab's machines stand idle sixteen hours a day and its cycle time says so — which is the era-honest reason fabs
          ran around the clock. Wages scale with coverage: the roster you see on the floor is one shift's worth of
          people, and the wage line pays it once per shift pattern.
        </p>
        <p>
          Staffing is therefore a slope with three axes: <strong>cost</strong> (a third of the wage bill per shift
          dropped), <strong>cycle time</strong> (idle machines are queueing lots), and <strong>quality</strong> — the
          night shift cuts more corners in the airlock, which feeds the same discipline → D₀ pipeline as every other
          hurry. The pattern is in the calibration signature: change it and the fab re-measures, because a fab on a
          different clock is a different fab.
        </p>
      </>
    ),
  },

  dimensions: {
    title: 'The market vector',
    body: (c) => (
      <>
        <p>
          A product's quality is a <strong>vector, not a scalar</strong> (M1.4). Each category authors its dimensions — a
          processor is scored on integer performance, floating point and power; a memory part on capacity, access speed and
          power — and a design carries a score on every axis. The buyers' logit weighs each axis by what <em>this era's</em>{' '}
          buyers care about: in 1975 working silicon is enough and price dominates; by the mid-80s the clock war is on. The
          weights drift week by week between authored era keyframes — data, not code.
        </p>
        <p>
          Scores scale with the node. A <em>speed</em>-shaped axis scales linearly with the feature size shrink; a{' '}
          <em>capacity</em>-shaped axis scales with its square — the same design shrunk one node carries about four times the
          cells, which is why each node's memory part obsoletes the last without any "generation" existing anywhere. Bins scale
          the clock-shaped axes (a down-bin is slower but still full-size) and run power the other way.
        </p>
        <p>
          The <strong>composite index</strong> is the one number the vector makes under this week's weights — what ignition
          thresholds, socket minimums and the "vs ambient" columns read. The ambient reference is a baseline part on the
          frontier node, so the whole industry's march raises the bar every year. Rivals read the same composites you do, and
          lay out refreshed designs — each archetype pushing the axes it believes in — when their parts fall behind.
        </p>
        {c && (
          <table>
            <thead>
              <tr>
                <th>category</th>
                <th>axes</th>
                <th>price weight</th>
              </tr>
            </thead>
            <tbody>
              {c.markets.map((m) => (
                <tr key={m.id}>
                  <td>{m.name}</td>
                  <td>
                    {m.dimensions
                      .map((d) => `${d.name} ${pctOfMille(d.weightMille, 0)} (${d.scaling})`)
                      .join(', ')}
                  </td>
                  <td>{pctOfMille(m.priceWeightMille, 0)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </>
    ),
  },

  inventory: {
    title: 'Inventory',
    body: (c) => (
      <>
        <p>
          Unsold units are no longer scrapped (M1.4): they go to <strong>stock</strong>, at a weekly carrying cost, and are
          offered again beside each week's production at your asks. A stocked part needs no obsolescence rule — its vector is
          frozen while the frontier moves, so it loses share-value at exactly the rate the industry improves, and the honest
          remedy for a bad ramp is clearance pricing the ladder's slow bins.
        </p>
        <p>
          The warehouse is the cycle's missing damper. The spot index reads the <strong>overhang</strong> — everyone's stock
          against weekly demand — beside the week's fresh imbalance: a glut deepens while stock drains instead of snapping
          back, and a shortage is met from warehouses before prices ratchet. Even a dead firm's warehouse sells down — the
          liquidator's trickle, landing exactly when a collapse takes a maker out.
        </p>
        <p>
          The cost model composes: materials are priced on wafers <em>started</em>; inventory prices wafers{' '}
          <em>finished and unsold</em> — which is what makes over-building capacity in a boom cost something after the boom is
          over. The bill lands on its own ledger line, and a slice of stock is written off every week: damage, spoilage, and
          parts that will never find a buyer.
        </p>
        {c && (
          <table>
            <thead>
              <tr>
                <th>category</th>
                <th>your stock</th>
                <th>overhang</th>
              </tr>
            </thead>
            <tbody>
              {c.markets
                .filter((m) => m.hasInventory)
                .map((m) => (
                  <tr key={m.id}>
                    <td>{m.name}</td>
                    <td>{int(m.playerStock)} units</td>
                    <td>{pctOfMille(m.overhangMille, 0)} of a week's demand</td>
                  </tr>
                ))}
            </tbody>
          </table>
        )}
      </>
    ),
  },

  floor: {
    title: 'The floor',
    body: (c) => {
      const site = c?.siteLine != null ? c.firm.lines.find((l) => l.index === c.siteLine) : undefined;
      return (
        <p>
          The floor explains itself in its own panels: the <strong>HUD</strong> for the frame and the boundary, the{' '}
          <strong>scoreboard</strong> for the brief, the three meters and what the last lever bought, <strong>site &amp; flow</strong>{' '}
          for the measured curve beside what fast-forward would say, the constraint and the crew, and the{' '}
          <strong>build palette</strong> for rooms, machines and moves. What the economy sees of all that is one{' '}
          <Link to="lines">line</Link>
          {site ? (
            <>
              {' '}
              — <em>{site.name}</em>, {site.nodeName}, dial {site.startsPerDay}/day, D₀ modifier {timesOfMille(site.lineD0Mille)},
              D₀ {d0OfMilli(site.d0Milli)}
            </>
          ) : null}
          .
        </p>
      );
    },
  },
};

export function Handbook() {
  const panels = usePanels();
  const hud = useSyncExternalStore(subscribe, snapshot);
  const id = panels.handbook;
  if (!id) return null;
  const page = PAGES[id] ?? {
    title: 'Not written yet',
    body: () => <p className="muted">There is no handbook page called “{id}” yet. Its package will write it.</p>,
  };
  return (
    <div className="handbook">
      <header>
        <h2>{page.title}</h2>
        <button className="close" title="close" onClick={() => closeHandbook()}>
          ×
        </button>
      </header>
      <nav>
        {Object.entries(PAGES).map(([key, p]) => (
          <button key={key} className={key === id ? 'active' : ''} onClick={() => openHandbook(key)}>
            {key === 'contents' ? 'contents' : p.title.toLowerCase()}
          </button>
        ))}
      </nav>
      <div className="handbook-body">{page.body(hud.campaign)}</div>
    </div>
  );
}
